import type { AdminEmployee, MonthlyRecord, EmployeeCategory } from '../types/models';
import type { FulltimePayroll, ParttimePayroll } from './PayrollTypes';
import {
  calculatePartSpecialAllowance,
  resolveFulltimeBenefitAmounts,
  resolveParttimeBenefitUnitPrices,
  type PayrollBenefitRuleOptions,
} from './PayrollBenefitRules';

/**
 * Determine employee category. Uses explicit employee_category if set,
 * otherwise derives from employmentType.
 */
export function determineCategory(employee: AdminEmployee): EmployeeCategory {
  if (employee.employeeCategory) return employee.employeeCategory;
  if (employee.employmentType === '\u6B63\u793E\u54E1') return 'fulltime';
  return 'parttime';
}

/**
 * Calculate meal deduction based on per-record shift type and meal flags.
 */
function calculateMealDeduction(
  employee: AdminEmployee,
  records: MonthlyRecord[]
): number {
  const mealPerDay = employee.mealDeductionPerDay ?? 250;
  let total = 0;
  for (const r of records) {
    // 休日出勤は食事控除対象外(労務士提出で別計上のため)
    if (r.shiftType === 'dayoff' || r.shiftType === 'paid' || r.shiftType === 'holiday_work') continue;
    // Day shift: check dayMealProvided
    if (r.shiftType === 'day') {
      if (!employee.dayMealProvided) continue;
      // long_shift_only: only deduct if totalMinutes >= 480 (8 hours)
      if (employee.mealCondition === 'long_shift_only' && r.totalMinutes < 480) continue;
      total += mealPerDay;
    }
    // Night shift: check nightMealProvided
    if (r.shiftType === 'night') {
      if (!employee.nightMealProvided) continue;
      total += mealPerDay;
    }
  }
  return total;
}

/**
 * Calculate fulltime payroll data from attendance records.
 */
export function calculateFulltimePayroll(
  employee: AdminEmployee,
  records: MonthlyRecord[],
  calculateOvertimeFn: (record: MonthlyRecord) => number,
  overrides: Map<string, number>,
  options?: PayrollBenefitRuleOptions
): FulltimePayroll {
  const dayRecords = records.filter(r => r.shiftType === 'day');
  const nightRecords = records.filter(r => r.shiftType === 'night');
  const paidRecords = records.filter(r => r.shiftType === 'paid');
  const holidayWorkRecords = records.filter(r => r.shiftType === 'holiday_work');

  // 休日出勤は出勤日数・出勤回数・総勤務時間から除外(労務士提出で別計上のため)
  const attendanceCount = dayRecords.length + nightRecords.length;
  const attendanceDays = dayRecords.length + (nightRecords.length * 2);
  const holidayWorkDays = holidayWorkRecords.length;
  const holidayWorkMinutes = holidayWorkRecords.reduce((sum, r) => sum + r.totalMinutes, 0);
  const paidLeaveDays = paidRecords.length;
  const nightShiftTotal = nightRecords.length;
  const nightShiftUpTo4 = Math.min(nightShiftTotal, 4);
  const nightShiftOver4 = Math.max(0, nightShiftTotal - 4);

  // 通常勤務(残業対象): day + night のみ
  const workRecords = records.filter(r => r.shiftType === 'day' || r.shiftType === 'night');
  const totalWorkMinutes = workRecords.reduce((sum, r) => sum + r.totalMinutes, 0);
  const overtimeMinutes = workRecords.reduce(
    (sum, record) => sum + Math.max(0, calculateOvertimeFn(record)), 0
  );

  // 交通費は休日出勤も対象
  const commuteCount = attendanceCount + holidayWorkDays;
  const autoMealDeduction = calculateMealDeduction(employee, records);
  const autoTransport = employee.commuteCostMonthlyFixed ?? ((employee.commuteCostYen ?? 0) * commuteCount);
  const benefitAmounts = resolveFulltimeBenefitAmounts(employee, options);

  return {
    employeeId: employee.id,
    employeeCode: employee.employeeCode ?? '',
    employeeName: employee.name,
    attendanceDays,
    attendanceCount,
    holidayWorkDays,
    holidayWorkMinutes,
    paidLeaveDays,
    absenceDays: overrides.get('absence_days') ?? 0,
    lateEarlyMinutes: overrides.get('late_early_minutes') ?? 0,
    totalWorkMinutes,
    overtimeMinutes,
    nightShiftUpTo4,
    nightShiftOver4,
    nightShiftTotal,
    specialBenefit: options?.benefitRuleEnabled ? benefitAmounts.specialBenefit : (overrides.get('special_benefit') ?? benefitAmounts.specialBenefit),
    specialAllowance: overrides.get('special_allowance') ?? benefitAmounts.specialAllowance,
    transportAllowance: overrides.get('transport_allowance') ?? autoTransport,
    mealDeduction: overrides.get('meal_deduction') ?? autoMealDeduction,
  };
}

/**
 * Calculate parttime payroll data from attendance records.
 */
export function calculateParttimePayroll(
  employee: AdminEmployee,
  records: MonthlyRecord[],
  calculateOvertimeFn: (record: MonthlyRecord) => number,
  overrides: Map<string, number>,
  options?: PayrollBenefitRuleOptions
): ParttimePayroll {
  const dayRecords = records.filter(r => r.shiftType === 'day');
  const nightRecords = records.filter(r => r.shiftType === 'night');
  const paidRecords = records.filter(r => r.shiftType === 'paid');
  const holidayWorkRecords = records.filter(r => r.shiftType === 'holiday_work');

  // 休日出勤は出勤日数・出勤回数から除外(労務士提出で別計上のため)
  const attendanceCount = dayRecords.length + nightRecords.length;
  const attendanceDays = dayRecords.length + (nightRecords.length * 2);
  const holidayWorkDays = holidayWorkRecords.length;
  const holidayWorkMinutes = holidayWorkRecords.reduce((sum, r) => sum + r.totalMinutes, 0);
  const paidLeaveDays = paidRecords.length;
  const attendanceTotal = attendanceCount;

  // 通常勤務(残業・手当対象): day + night のみ
  const workRecords = records.filter(r =>
    r.shiftType === 'day' || r.shiftType === 'night'
  );
  const totalWorkMinutes = workRecords.reduce((sum, r) => sum + r.totalMinutes, 0);
  const overtimeMinutes = workRecords.reduce(
    (sum, r) => sum + Math.max(0, calculateOvertimeFn(r)), 0
  );

  const totalWorkHours = totalWorkMinutes / 60;
  const benefitUnitPrices = resolveParttimeBenefitUnitPrices(employee, options);
  // From 2026-09-16 the two allowances are paid as one: rounded hours * the employee's unit price.
  const partSpecialAllowance = options?.partSpecialAllowanceEnabled
    ? calculatePartSpecialAllowance(employee, totalWorkMinutes)
    : null;
  const autoSpecialBenefit = partSpecialAllowance
    ? 0
    : Math.round(totalWorkHours * benefitUnitPrices.specialBenefitUnitPrice);
  const autoSpecialAllowance = partSpecialAllowance
    ? partSpecialAllowance.amount
    : Math.round(totalWorkHours * benefitUnitPrices.specialAllowanceUnitPrice);
  const autoMealDeduction = calculateMealDeduction(employee, records);
  // 交通費は休日出勤も対象
  const commuteCount = attendanceCount + holidayWorkDays;
  const autoTransport = employee.commuteCostMonthlyFixed ?? ((employee.commuteCostYen ?? 0) * commuteCount);

  return {
    employeeId: employee.id,
    employeeCode: employee.employeeCode ?? '',
    employeeName: employee.name,
    attendanceDays,
    holidayWorkDays,
    holidayWorkMinutes,
    paidLeaveDays,
    absenceDays: overrides.get('absence_days') ?? 0,
    lateEarlyMinutes: overrides.get('late_early_minutes') ?? 0,
    totalWorkMinutes,
    overtimeMinutes,
    attendanceTotal,
    specialBenefit: partSpecialAllowance ? 0 : (overrides.get('special_benefit') ?? autoSpecialBenefit),
    specialAllowance: overrides.get('special_allowance') ?? autoSpecialAllowance,
    transportAllowance: overrides.get('transport_allowance') ?? autoTransport,
    mealDeduction: overrides.get('meal_deduction') ?? autoMealDeduction,
  };
}

/**
 * Get the auto-calculated value for a field (before override).
 * Used to show "original value" in the UI when an override exists.
 */
export function getAutoValue(
  category: EmployeeCategory,
  fieldName: string,
  employee: AdminEmployee,
  records: MonthlyRecord[],
  calculateOvertimeFn: (record: MonthlyRecord) => number,
  options?: PayrollBenefitRuleOptions
): number {
  const emptyOverrides = new Map<string, number>();

  if (category === 'fulltime') {
    const result = calculateFulltimePayroll(employee, records, calculateOvertimeFn, emptyOverrides, options);
    return (result as any)[fieldNameToPayrollKey(fieldName)] ?? 0;
  }
  if (category === 'parttime') {
    const result = calculateParttimePayroll(employee, records, calculateOvertimeFn, emptyOverrides, options);
    return (result as any)[fieldNameToPayrollKey(fieldName)] ?? 0;
  }
  return 0;
}

function fieldNameToPayrollKey(fieldName: string): string {
  const map: Record<string, string> = {
    absence_days: 'absenceDays',
    late_early_minutes: 'lateEarlyMinutes',
    special_benefit: 'specialBenefit',
    special_allowance: 'specialAllowance',
    transport_allowance: 'transportAllowance',
    meal_deduction: 'mealDeduction',
  };
  return map[fieldName] ?? fieldName;
}
